import VSpacingContainer from 'bloko/blocks/vSpacing/VSpacingContainer';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { GbbGoodFeature } from 'src/components/Price/Gbb';
import PeriodInDays, { PeriodVariants } from 'src/components/Price/PeriodInDays';
import useContactsCount, { ContactsCountVariants } from 'src/components/Price/Tariff/useContactsCount';
import useOptionsCount from 'src/components/Price/Tariff/useOptionsCount';
import usePublicationsCount from 'src/components/Price/Tariff/usePublicationsCount';
import translation from 'src/components/translation';
import { useIsZarplataPlatform } from 'src/hooks/usePlatform';
import { publicationsCountFields, Tariff } from 'src/models/price/priceZp';
import { BooleanString, IntervalInSecondsString } from 'src/models/price/product.types';
import { getPublicationFeatures, isUnlimitedResumeAccess } from 'src/utils/price/products';

const TrlKeys = {
    access: 'cvmarket.service.db',
    logo: 'price.tariffFeature.bundles.logo',
    manualUpdateInterval: {
        [IntervalInSecondsString.OneDay]: 'price.tariffFeature.bundles.manualUpdateInterval.1d',
    },
    autoUpdateInterval: {
        [IntervalInSecondsString.ThreeDays]: 'price.tariffFeature.bundles.autoUpdateInterval.3d',
    },
};

const BundlesFeatures: TranslatedComponent<{ tariff: Tariff }> = ({ tariff, trls }) => {
    const isZP = useIsZarplataPlatform();
    const zpUnlimitedResumeAccess = isZP && isUnlimitedResumeAccess(tariff.product);
    const publicationsCount = usePublicationsCount(tariff.product);
    const optionsCount = useOptionsCount(tariff.product);
    const contactsCount = useContactsCount(tariff.product, {
        noUnlimited: tariff.backoffice,
        variant: ContactsCountVariants.Detailed,
    });

    // Выделение логотипом
    const renderLogoFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const hasLogo = tariff.product && getPublicationFeatures(tariff.product)?.logo;
        return hasLogo === BooleanString.True ? <GbbGoodFeature>{trls[TrlKeys.logo]}</GbbGoodFeature> : null;
    };

    // Автоподнятие на первые страницы поиска раз в 3 дня
    const renderAutoUpdateIntervalFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const trlKeys = TrlKeys.autoUpdateInterval;
        const interval =
            tariff.product && (getPublicationFeatures(tariff.product)?.autoUpdateInterval as keyof typeof trlKeys);
        return interval && interval in trlKeys ? <GbbGoodFeature>{trls[trlKeys[interval]]}</GbbGoodFeature> : null;
    };

    // Поднятие на первые страницы поиска раз в 24 часа вручную
    const renderManualUpdateIntervalFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const trlKeys = TrlKeys.manualUpdateInterval;
        const interval =
            tariff.product && (getPublicationFeatures(tariff.product)?.manualUpdateInterval as keyof typeof trlKeys);
        return interval && interval in trlKeys ? <GbbGoodFeature>{trls[trlKeys[interval]]}</GbbGoodFeature> : null;
    };

    // 2 опции «Премиум»
    const renderOptionsCountFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        return !tariff.fields.optionCount && optionsCount ? <GbbGoodFeature>{optionsCount}</GbbGoodFeature> : null;
    };

    // 365 дней
    const renderPeriodFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        return !tariff.fields.period && tariff.product?.period ? (
            <GbbGoodFeature>
                <PeriodInDays period={tariff.product.period} variant={PeriodVariants.Bundle} />
            </GbbGoodFeature>
        ) : null;
    };

    // 100 контактов резюме / Сколько угодно резюме
    const renderCivCountFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        return !tariff.fields.civCount && contactsCount ? (
            <GbbGoodFeature>{zpUnlimitedResumeAccess ? trls[TrlKeys.access] : contactsCount}</GbbGoodFeature>
        ) : null;
    };

    // 10 вакансий «Бизнес»
    const renderPublicationsFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        const hasCountField = publicationsCountFields.some((fieldName) => tariff.fieldNames.includes(fieldName));
        return !hasCountField && publicationsCount ? <GbbGoodFeature>{publicationsCount}</GbbGoodFeature> : null;
    };

    return (
        <VSpacingContainer base={4}>
            {renderPublicationsFeature()}
            {renderOptionsCountFeature()}
            {renderLogoFeature()}
            {renderAutoUpdateIntervalFeature()}
            {renderManualUpdateIntervalFeature()}
            {renderCivCountFeature()}
            {renderPeriodFeature()}
        </VSpacingContainer>
    );
};

export default translation(BundlesFeatures);
